import React from 'react';
import update from 'immutability-helper';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from 'react-intl';
import { Button, Popover } from 'react-bootstrap';
import { DropTarget } from 'react-dnd';
import ScrollArea from 'react-scrollbar';
import { get as _get } from 'lodash';
import { animateScroll as scroll } from 'react-scroll';

import { getCmsDataTree } from 'helpers/cms';
import { updateWidthAndHeight } from 'actions/LayoutActions';

import EntityPropsUpdater from 'component/EntityPropsUpdater';

import CmsPageTreeViewSlideout from 'components/cms/CmsPageTreeViewSlideout';
import CmsPageQuickPageBuilder from 'components/cms/CmsPageQuickPageBuilder';
import CmsElement from 'components/cms/CmsElement';
import CmsNewGrid from 'components/cms/CmsNewGrid';

import CmsBlankPage from 'components/layout/hint/CmsBlankPage';

import CmsElementSettings from 'components/cms/settings/CmsElementSettings';

import OverlayTrigger from 'components/OverlayTrigger';

const target = {
  hover(props, monitor, component) {
    const tolerance = 100;
    const { y: mouseY } = monitor.getClientOffset();

    if (mouseY < 60 + tolerance) {
      // 60 for the menu
      // move up
      window.scrollBy(0, -1);
    } else if (props.layout.height - mouseY < tolerance) {
      // move down
      window.scrollBy(0, 1);
    }

    if (component.state.showSettings) {
      component.setState({ showSettings: false, settingsIndex: null });
    }
  },
};

@DropTarget(['grid', 'row', 'element'], target, connect => ({
  connectDropTarget: connect.dropTarget(),
}))
class CmsCanvas extends EntityPropsUpdater {
  constructor(props) {
    super(props);

    this.state = {
      showSettings: false,
      settingsIndex: null,
    };

    this.containerRef = React.createRef();
  }

  componentDidUpdate = prevProps => {
    const { showTreeViewSlideout, showQuickPageBuilder } = this.props;

    if (!prevProps.showTreeViewSlideout && showTreeViewSlideout) {
      this.setState({ showSettings: false });
    }

    if (!prevProps.showQuickPageBuilder && showQuickPageBuilder) {
      this.setState({ showSettings: false });
    }
  };

  // add element at the end
  addElement = (index, elementData) => {
    const { entity, changeEntity } = this.props;
    const { element, tree } = this.getElementInfos(index);

    let newValue = element.getChildren();
    newValue = !newValue ? [] : newValue.slice();
    const value = entity.createValue('content', [], elementData);
    newValue.push(value);

    const newEntity = { ...entity.updateValue('children', newValue, tree) };

    changeEntity(newEntity, () => {
      const { layout, onElementAdded, doUpdateWidthAndHeight } = this.props;

      if (onElementAdded) {
        onElementAdded();
      }

      doUpdateWidthAndHeight(layout.width, layout.height);
    });
  };

  reorderElements = (fromIndex, toIndex, direction, sameContainer) => {
    // console.log(fromIndex + ' ' + toIndex + ' ' + direction)
    const { element: fromElement, tree: fromTree } = this.getElementInfos(fromIndex);
    const { element: toElement, tree: toTree } = this.getElementInfos(toIndex);
    const { entity, changeEntity } = this.props;

    const fromContainerIndex = fromIndex.substring(fromIndex.lastIndexOf('.')).replace('.', '');
    const toContainerIndex = toIndex.substring(toIndex.lastIndexOf('.')).replace('.', '');

    if (sameContainer) {
      // get container
      const containerIndex = fromIndex.substring(0, fromIndex.lastIndexOf('.'));
      let children;
      let containerName = 'children';
      let tree = [];
      if (containerIndex === '') {
        children = { children: entity.getContent().slice() };
        containerName = 'content';
      } else {
        const { element, tree: elementTree } = this.getElementInfos(containerIndex);
        children = { children: element.getChildren().slice() };
        tree = elementTree;
      }

      ({ children } = update(children, {
        children: {
          $splice: [[fromContainerIndex, 1], [toContainerIndex, 0, fromElement]],
        },
      }));

      const newEntity = { ...entity.updateValue(containerName, children, tree) };

      changeEntity(newEntity, () => {
        const { onElementReordered } = this.props;

        if (onElementReordered) {
          onElementReordered();
        }
      });
    } else {
      // get the from container and remove the element
      const propertyNames = [];
      const values = [];
      const fieldTrees = [];

      let containerIndex = fromIndex.substring(0, fromIndex.lastIndexOf('.'));
      if (containerIndex === '') {
        const newValue = entity.getContent().slice();
        newValue.splice(fromContainerIndex, 1);

        propertyNames.push('content');
        fieldTrees.push([]);
        values.push(newValue);
      } else {
        const { element: fromContainer, tree: fromTree } = this.getElementInfos(containerIndex);
        const newValue = fromContainer.getChildren().slice();
        newValue.splice(fromContainerIndex, 1);

        propertyNames.push('children');
        fieldTrees.push(fromTree);
        values.push(newValue);
      }

      // get the to container and add the element
      containerIndex = toIndex.substring(0, toIndex.lastIndexOf('.'));
      if (containerIndex === '') {
        const newValue = entity.getContent().slice();
        newValue.splice(toContainerIndex + (direction === 'after' ? 1 : 0), 0, fromElement);

        propertyNames.push('content');
        fieldTrees.push([]);
        values.push(newValue);
      } else {
        const { element: toContainer, tree: toTree } = this.getElementInfos(containerIndex);
        const newValue = toContainer.getChildren().slice();
        newValue.splice(toContainerIndex + (direction === 'after' ? 1 : 0), 0, fromElement);

        propertyNames.push('children');
        fieldTrees.push(toTree);
        values.push(newValue);
      }

      this.updateEntityMultiple(propertyNames, values, fieldTrees, () => {
        const { onElementReordered } = this.props;

        if (onElementReordered) {
          onElementReordered();
        }
      });
    }
  };

  removeElement = (index, closeSettings = true, callback = null) => {
    const { entity, changeEntity } = this.props;

    const containerIndex = index.toString().substring(0, index.toString().lastIndexOf('.'));
    const elementIndex = index
      .toString()
      .substring(index.toString().lastIndexOf('.'))
      .replace('.', '');

    const treeIndex = containerIndex.toString().split('.');
    const tree = [];

    let parentElement = null;
    if (containerIndex !== '') {
      for (let i = 0; i < treeIndex.length; i++) {
        parentElement = i === 0 ? entity.getContent() : parentElement.getChildren();
        tree.push(i === 0 ? 'content' : 'children');
        tree.push(treeIndex[i]);
        parentElement = parentElement[treeIndex[i]];
      }
    }

    let newEntity;
    if (parentElement === null) {
      // remove a grid
      const newValue = entity.getContent().slice();
      newValue.splice(elementIndex, 1);

      newEntity = { ...entity.updateValue('content', newValue) };
    } else {
      // remove an element
      const newValue = parentElement.getChildren().slice();
      newValue.splice(elementIndex, 1);

      newEntity = { ...entity.updateValue('children', newValue, tree) };
    }

    if (closeSettings) {
      this.closeSettings();
    }
    changeEntity(newEntity, () => {
      const { layout, onElementAdded, doUpdateWidthAndHeight } = this.props;

      if (onElementAdded) {
        onElementAdded();
      }

      doUpdateWidthAndHeight(layout.width, layout.height);
    });

    if (callback) {
      callback();
    }
  };

  addDynamicElement = dynamicElement => {
    const { entity, changeEntity } = this.props;
    const data = {
      type: 'dynamic_element',
      dynamic_element_content: dynamicElement.content,
      params: [{ name: 'dynamicElementId', value: dynamicElement.id }],
    };
    const newValue = entity.getContent().slice();
    const value = entity.createValue('content', [], data);
    newValue.push(value);

    const newEntity = { ...entity.updateValue('content', newValue) };

    changeEntity(newEntity, () => {
      const { layout, onElementAdded, doUpdateWidthAndHeight } = this.props;

      if (onElementAdded) {
        onElementAdded();
      }

      doUpdateWidthAndHeight(layout.width, layout.height);
    });
  };

  // add grid at the end
  addGrid = (params = null) => {
    const data = {
      type: 'grid',
      params,
    };

    this.addCmsElements([data]);
  };

  addCmsElements = content => {
    const { entity, changeEntity } = this.props;

    const newValue = entity.getContent().slice();
    const previousElemsCount = newValue.length;

    for (let i = 0; i < content.length; i++) {
      const data = content[i];
      const value = entity.createValue('content', [], data);
      newValue.push(value);
    }

    const newEntity = { ...entity.updateValue('content', newValue) };

    changeEntity(newEntity, () => {
      const { layout, onElementAdded, doUpdateWidthAndHeight } = this.props;

      if (onElementAdded) {
        onElementAdded();
      }

      doUpdateWidthAndHeight(layout.width, layout.height);

      const navToElement = _get(
        this.containerRef,
        'current.firstChild.firstChild.firstChild.childNodes.' + previousElemsCount
      );

      if (navToElement) {
        const rect = navToElement.getBoundingClientRect();
        scroll.scrollTo(window.scrollY + rect.top - 100);
      }
    });
  };

  showElementSettings = (e, settingsIndex) => {
    e.preventDefault();
    e.stopPropagation();

    this.setState({ showSettings: true, settingsIndex }, () => {
      const { closeTreeViewSlideout, closeQuickPageBuilder, onSettingsOpen } = this.props;

      if (closeTreeViewSlideout) {
        closeTreeViewSlideout(e);
      }
      if (closeQuickPageBuilder) {
        closeQuickPageBuilder(e);
      }
      if (onSettingsOpen) {
        onSettingsOpen();
      }
    });
  };

  closeSettings = (e = null) => {
    if (e) {
      e.preventDefault();
    }

    this.setState({ showSettings: false, settingsIndex: null }, () => {
      const { onSettingsClose } = this.props;

      if (onSettingsClose) {
        onSettingsClose();
      }
    });
  };

  getSettingsElement = () => {
    const index = this.state.settingsIndex;

    if (index === null) {
      return null;
    }

    const { element } = this.getElementInfos(index);
    return element;
  };

  getElementInfos = index => {
    const { entity } = this.props;
    const treeIndex = index.toString().split('.');
    const tree = [];

    let element = null;
    for (let i = 0; i < treeIndex.length; i++) {
      if (i > 0 && !element) {
        return { element: null, tree: [] };
      }

      element = i === 0 ? entity.getContent() : element.getChildren();
      tree.push(i === 0 ? 'content' : 'children');
      tree.push(treeIndex[i]);
      element = element[treeIndex[i]];
    }

    return { element, tree };
  };

  render() {
    const {
      intl: { messages },
      intl,
      appIntl,
      entity,
      changeEntity,
      editMode,
      effectsEnabled,
      layout,
      connectDropTarget,
      showTreeViewSlideout,
      closeTreeViewSlideout,
      showQuickPageBuilder,
      closeQuickPageBuilder,
    } = this.props;
    const { showSettings, settingsIndex } = this.state;

    const cmsData = getCmsDataTree(entity.getContent());

    let scale = 1;
    let clippingHeight = 'auto';
    // let scaledWidth = 'auto';
    const slideoutWidth = 450;
    const containerWidth = (this.containerRef.current && this.containerRef.current.scrollWidth) || 'auto';
    // const containerHeight = (this.containerRef.current && this.containerRef.current.scrollHeight) || 'auto';
    if (layout.breakpointIndex > 1 && (showTreeViewSlideout || showQuickPageBuilder || showSettings)) {
      /* switch (layout.breakpointIndex) {
        case 3: {
          if (containerWidth < 1200) { // 1200 is lg
            scaledWidth = 1220;
          }
          break;
        }

        case 2: {
          if (containerWidth < 992) { // 992 is md
            scaledWidth = 1002;
          }
        }
      }
      scale = (scaledWidth - slideoutWidth) / scaledWidth; */
      scale = (containerWidth - slideoutWidth) / containerWidth;

      // find the DOM node that is scaled down childNodes
      const scaledElement = _get(this.containerRef, 'current.firstChild.firstChild.firstChild');
      if (scaledElement) {
        clippingHeight = scaledElement.scrollHeight;
      }
    }

    return connectDropTarget(
      <div className="ms-canvas relative" ref={this.containerRef}>
        <div
          style={{
            width: containerWidth !== 'auto' ? containerWidth * scale + 'px' : containerWidth,
            height: clippingHeight !== 'auto' ? clippingHeight * scale + 150 + 'px' : clippingHeight,
          }}
          className="no-overflow"
        >
          <div
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              width: containerWidth !== 'auto' ? containerWidth + 'px' : containerWidth,
            }}
          >
            <div className="themed">
              {cmsData.map((element, i) => {
                return (
                  <CmsElement
                    canvasContainerRef={this.containerRef}
                    key={element.id || i}
                    addElement={this.addElement}
                    removeElement={this.removeElement}
                    reorderElements={this.reorderElements}
                    showElementSettings={this.showElementSettings}
                    editMode={editMode}
                    effectsEnabled={effectsEnabled}
                    index={i.toString()}
                    intl={intl}
                    appIntl={appIntl}
                    layout={layout}
                    scale={scale}
                    {...element}
                  />
                );
              })}
            </div>

            {editMode && entity.getContent().length === 0 && (
              <CmsBlankPage
                action={() =>
                  this.addGrid([
                    { name: 'isFluid', value: 'yes' },
                    { name: 'paddingTop', value: 50 },
                    { name: 'paddingBottom', value: 50 },
                  ])
                }
              />
            )}

            {editMode && !this.props.disableNewContainer && (
              <div className="p-t-30 p-b-30 text-center">
                <OverlayTrigger
                  container={() => this.containerRef.current}
                  delayHide={150}
                  placement="bottom"
                  trigger="click"
                  rootClose
                  wrappingComponent={Popover}
                  id="add_container"
                  overlay={<CmsNewGrid addGrid={this.addGrid} addDynamicElement={this.addDynamicElement} />}
                >
                  <Button
                    bsStyle="primary"
                    className="btn-rounded"
                    style={{
                      transform: `scale(${1 / scale})`,
                    }}
                  >
                    {messages.cms_add_container}
                  </Button>
                </OverlayTrigger>
              </div>
            )}
          </div>
        </div>

        <div
          id="page-quickview"
          className={
            'quickview-wrapper ' + (showTreeViewSlideout || showQuickPageBuilder || showSettings ? 'open ' : '')
          }
        >
          {(showTreeViewSlideout || showQuickPageBuilder) && (
            <div className="p-t-10 p-b-10 p-l-20 p-r-20 bg-master">
              <span className="text-white block overflow-ellipsis p-r-20">
                {showTreeViewSlideout && 'Page structure'}
                {showQuickPageBuilder && 'Quick page builder'}
              </span>
              <a
                href="#close"
                className="close btn-link quickview-toggle pull-right text-white"
                onClick={
                  (showTreeViewSlideout && closeTreeViewSlideout) || (showQuickPageBuilder && closeQuickPageBuilder)
                }
              >
                <span>×</span>
              </a>
            </div>
          )}
          <div className="tab-content" style={{ height: '100%' }}>
            <ScrollArea
              speed={0.8}
              className="scroll-wrapper"
              contentClassName=""
              horizontal={false}
              style={{ overflow: 'hidden', height: '100%' }}
            >
              <div className="p-b-50">
                <CmsElementSettings
                  open={showSettings}
                  navigateSettings={this.showElementSettings}
                  close={this.closeSettings}
                  element={this.getSettingsElement()}
                  entity={entity}
                  changeEntity={changeEntity}
                  addElement={this.addElement}
                  removeElement={this.removeElement}
                  reorderElements={this.reorderElements}
                  index={settingsIndex}
                  pageId={this.props.pageId}
                  itemId={this.props.itemId}
                  key={'settings-' + settingsIndex + appIntl.displayIntlForm}
                />
                <CmsPageTreeViewSlideout
                  open={showTreeViewSlideout}
                  close={closeTreeViewSlideout}
                  page={entity}
                  navigateSettings={this.showElementSettings}
                />
                <CmsPageQuickPageBuilder
                  open={showQuickPageBuilder}
                  close={closeQuickPageBuilder}
                  page={entity}
                  addCmsElements={this.addCmsElements}
                />
              </div>
            </ScrollArea>
          </div>
        </div>
      </div>
    );
  }
}
CmsCanvas.defaultProps = {
  pageId: null, // data that gets passed to media selector; needed to assign links to media files; in this case the file will be assigned to a page
  itemId: null, // data that gets passed to media selector; needed to assign links to media files
  disableNewContainer: false, // indicate that adding new container button will not be shown
  effectsEnabled: true,
};

const mapStateToProps = state => {
  return {
    appIntl: state.intl,
    layout: state.layout,
    cmsSettingsPosition: state.cms.settingsPosition,
  };
};
const mapDispatchToProps = dispatch => {
  return {
    doUpdateWidthAndHeight: (width, height) => {
      dispatch(updateWidthAndHeight(width, height));
    },
  };
};

export default withRouter(
  injectIntl(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(CmsCanvas)
  )
);
